import { useEffect, useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";

import logo1 from "../../../assets/imgs/atribot.png";

import Box from "@mui/material/Box";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";

import VideocamIcon from "@mui/icons-material/Videocam";
import MicIcon from "@mui/icons-material/Mic";
import TextsmsIcon from "@mui/icons-material/Textsms";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import { IS_MOBILE } from "../../../constants/breakpoints";

const actions = [
    { icon: <TextsmsIcon />, name: "گفتگوی متنی", color: "#0b819d", hoverColor: "#006363", url: "https://genui-roan.vercel.app" }, // Blue - Added URL
    { icon: <MicIcon />, name: "گفتگوی صوتی", color: "#43a047", hoverColor: "#2e7031", url: "https://voice-genui.vercel.app/" }, // Green - Added URL
    { icon: <VideocamIcon />, name: "بزودی... گفتگوی تصویری", color: "#e53935", hoverColor: "#9a2424" }, // Red - No URL for now
];

const Chatbot = () => {
    const [chatbotUrl, setChatbotUrl] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if the screen is small (mobile)

    const openChatbot = url => {
        setChatbotUrl(url);
    };

    const closeChatbot = () => {
        setChatbotUrl(null);
    };

    useEffect(() => {
        const handlePostMessage = async event => {
            // --- Origin Check ---
            const allowedOrigins = [
                "https://genui-roan.vercel.app", //text-chat
                "https://voice-genui.vercel.app", //voice-chat
                "http://localhost:3000", //development
            ];
            if (!allowedOrigins.includes(event.origin) && event.origin !== "") {
                console.log("Message origin not allowed. Ignoring.");
                return;
            }

            // --- Destructure Data ---
            // Ensure event.data exists and has type/payload
            if (!event.data || !event.data.type || !event.data.payload) {
                return;
            }
            const { type, payload } = event.data;
            console.log("Received message:", { type, payload: JSON.stringify(payload, null, 2) });
            console.log("Processing message type:", type);

            if (type === "SELECTED_FLIGHT") {
                // Domestic flight
                sessionStorage.setItem("selectedFlight", JSON.stringify(payload.selectedFlight));
                sessionStorage.setItem("generalInformation", JSON.stringify(payload.generalInformation));
                sessionStorage.setItem("domesticFlightInformation", JSON.stringify(payload.domesticFlightInformation));
                window.location.href = "/fa/flight-passengers";
            } else if (type === "SELECTED_INT_FLIGHT") {
                // International flight
                sessionStorage.setItem("selectedIntFlight", JSON.stringify(payload.selectedIntFlight));
                sessionStorage.setItem("generalInformation", JSON.stringify(payload.generalInformation));
                sessionStorage.setItem("intFlightInformation", JSON.stringify(payload.intFlightInformation));
                window.location.href = "/fa/int-flight-passengers";
            } else if (type === "SELECTED_INT_HOTEL") {
                // --- Set Session Storage ---
                sessionStorage.setItem("selectedHotel", JSON.stringify(payload.selectedHotel));
                sessionStorage.setItem("generalInformation", JSON.stringify(payload.generalInformation));
                sessionStorage.setItem("intHotelInformation", JSON.stringify(payload.intHotelInformation));

                // --- Extract Data for API Call ---
                const hotelId = payload.selectedHotel.hotel_id;
                window.location.href = `/fa/hotel?hotelId=${hotelId}&action=intRooms`;
            } else if (type === "SELECTED_HOTEL") {
                // Handle domestic hotel selection
                if (!payload.selectedHotel || !payload.generalInformation || !payload.hotelInformation) {
                    console.error("Missing required payload data:", payload);
                    return;
                }

                sessionStorage.setItem("selectedHotel", JSON.stringify(payload.selectedHotel));
                sessionStorage.setItem("generalInformation", JSON.stringify(payload.generalInformation));
                sessionStorage.setItem("hotelInformation", JSON.stringify(payload.hotelInformation));

                const hotelId = payload.selectedHotel.hotel_id;
                if (!hotelId) {
                    console.error("Hotel ID is missing");
                    return;
                }

                window.location.href = IS_MOBILE
                    ? `/fa/hotel?hotelId=${hotelId}&action=hotel-details`
                    : `/fa/hotel?hotelId=${hotelId}&action=rooms`;
            } else {
                console.log("Unhandled message type:", type);
            }
        };
        window.addEventListener("message", handlePostMessage);

        return () => {
            window.removeEventListener("message", handlePostMessage);
        };
    }, []);

    return (
        <>
            <Box sx={{ flexGrow: 1, position: "fixed", bottom: 16, right: 16, zIndex: 500 }}>
                <SpeedDial
                    ariaLabel="SpeedDial chatbot options"
                    icon={<img src={logo1} alt="chatbot" height={45} width={37} />}
                    FabProps={{
                        sx: {
                            backgroundColor: "#F57C00",
                            "&:hover": {
                                backgroundColor: "#bf6001",
                            },
                        },
                    }}
                >
                    {actions.map(action => (
                        <SpeedDialAction
                            key={action.name}
                            icon={action.icon}
                            tooltipTitle={action.name}
                            onClick={action.url ? () => openChatbot(action.url) : undefined}
                            FabProps={{
                                sx: {
                                    // backgroundColor: "transparent",
                                    boxShadow: "none",
                                    color: action.color,
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        color: action.hoverColor,
                                    },
                                    "& .MuiSvgIcon-root": {
                                        fontSize: "2rem",
                                    },
                                },
                            }}
                        />
                    ))}
                </SpeedDial>
            </Box>
            {/* Chatbot iframe: Render based on chatbotUrl state */}
            {chatbotUrl && (
                <ClickAwayListener onClickAway={closeChatbot}>
                    <Box
                        sx={{
                            position: "fixed",
                            // Adjusted bottom position to be just above the SpeedDial FAB
                            bottom: 80, // 16 (SpeedDial bottom) + 56 (FAB height) + 8 (margin)
                            right: 16,
                            width: isMobile ? "90%" : 400,
                            height: isMobile ? "60%" : 500,
                            backgroundColor: "white",
                            boxShadow: 3,
                            borderRadius: 2,
                            overflow: "hidden",
                            zIndex: 1400,
                        }}
                    >
                        <iframe
                            src={chatbotUrl}
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                            }}
                            title="Chatbot"
                            allow="microphone"
                        ></iframe>
                    </Box>
                </ClickAwayListener>
            )}
        </>
    );
};

export default Chatbot;
